import { safeLocalStorage } from '@toss/storage';
import {
  API_ENDPOINT,
  type ClientApiV1UserDevicesConfirmTokenCreateResponse,
  type ClientApiV1UserDevicesRegisterCreate,
  type ClientApiV1UserDevicesRegisterCreateVariables,
  ResponseStatus,
} from '../../../../../../../resources/@types/uccSchema';
import { uccFetcher } from '../../../../../../../resources/Fetcher';
import { logger } from '../../../../../../../resources/Logger';

import qs from 'query-string';
import { ApiError } from '../../../../../../../resources/ApiError';
import { getDeviceTokenStorageKey } from '../getDeviceTokenStorageKey';

/**
 * POST - /user-devices/register
 * 로컬에 가지고 있는 토큰을 보내 등록된 기기인지 확인
 * 등록되지 않은 기기면 응답에 토큰이 오고 등록됨 (리프레시토큰도 오긴할듯)
 *
 * 과거 자주 호출하는 api에 mutation을 사용했다가 실패율이 높아진 케이스가 있어서 fetcher사용
 * https://inflab-team.slack.com/archives/C04RK197KD1/p1737538078878189 참고
 */
export default function useDeviceRegisterCheck() {
  const checkDeviceRegister = async ({ userId }: { userId: number }) => {
    const storageKey = getDeviceTokenStorageKey(userId);
    const token = safeLocalStorage.get(storageKey) ?? '';

    const response = await fetchDeviceRegisterCheck({ token }, {});

    if (response.isMaxRegisteredDevices) {
      return;
    }

    if (response.token) {
      const confirmResponse = await fetchDeviceRegisterTokenConfirm({ token: response.token });
      if (confirmResponse.result) {
        safeLocalStorage.set(storageKey, response.token);
      }
    }
  };

  return {
    checkDeviceRegister,
  };
}

async function fetchDeviceRegisterCheck(
  query: {
    token: string;
  },
  payload: ClientApiV1UserDevicesRegisterCreateVariables,
) {
  try {
    const response = await uccFetcher.post<ClientApiV1UserDevicesRegisterCreate>(
      qs.stringifyUrl({
        url: API_ENDPOINT.CLIENT_V1_USER_DEVICES_REGISTER,
        query,
      }),
      payload,
    );

    return {
      token: response.data?.token,
      isMaxRegisteredDevices: false,
    };
  } catch (error) {
    if (ApiError.isApiError(error)) {
      if (error.errorCode === 'MAX_REGISTERED_DEVICES') {
        return {
          token: null,
          isMaxRegisteredDevices: true,
        };
      }
      logger.error(error);
    }

    return {
      token: null,
      isMaxRegisteredDevices: false,
    };
  }
}

async function fetchDeviceRegisterTokenConfirm(payload: {
  token: string;
}) {
  try {
    const response = await uccFetcher.post<ClientApiV1UserDevicesConfirmTokenCreateResponse>(
      API_ENDPOINT.CLIENT_V1_USER_DEVICES_CONFIRM_TOKEN,
      payload,
    );

    return {
      result: response.statusCode === ResponseStatus.OK,
    };
  } catch (error) {
    if (ApiError.isApiError(error)) {
      logger.error(error);
    }

    return {
      result: false,
    };
  }
}
